import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { GoonAPIClient, UserProfile } from '../client/ApiClient';
import { API_ENDPOINT } from '../constants';
import { prepareImageAsWebP } from '../utils/webp';

@customElement('my-profiles')
export class MyProfiles extends LitElement {
  createRenderRoot() { return this; }

  @state() profiles: UserProfile[] = [];
  @state() activeProfile: UserProfile | null = null;
  @state() showCreateModal = false;
  @state() showEditModal = false;
  @state() currentEditProfile: UserProfile | null = null;
  @state() displayName = '';
  @state() avatarFile = null;
  @state() avatarPreviewUrl = '';
  @state() error = '';

  async firstUpdated() {
    await this.loadProfiles();
  }

  async loadProfiles() {
    try {
      const client = new GoonAPIClient(API_ENDPOINT);
      const res = await client.getMyProfiles();
      this.profiles = res.profiles;
      const current = await client.getCurrentUserProfile();
      this.activeProfile = current;
    } catch (err) {
      console.error('Failed to load profiles', err);
    }
  }

  async switchProfile(profileId: string) {
    try {
      const client = new GoonAPIClient(API_ENDPOINT);
      await client.switchProfile(profileId);
      location.reload();
    } catch (err) {
      console.error('Failed to switch profile', err);
    }
  }

  openCreateModal() {
    this.displayName = '';
    this.avatarFile = null;
    this.avatarPreviewUrl = '';
    this.error = '';
    this.showCreateModal = true;
  }

  openEditModal(profile: UserProfile) {
    this.currentEditProfile = profile;
    this.displayName = profile.display_name;
    this.avatarFile = null;
    this.avatarPreviewUrl = profile.avatar_url || '';
    this.error = '';
    this.showEditModal = true;
  }

  handleFileUpload(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.avatarFile = file;
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        this.avatarPreviewUrl = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  async saveProfile(isNew: boolean) {
    try {
      const client = new GoonAPIClient(API_ENDPOINT);
      let imageUrl = undefined;

      if (this.avatarFile) {
        const webpBlob = await prepareImageAsWebP(this.avatarFile, 256, 256);
        const webpFile = new File([webpBlob], 'avatar.webp', { type: 'image/webp' });
        const mediaUrl = await client.uploadMediaAndWait(webpFile, 'persona', 'image/webp');
        imageUrl = mediaUrl;
      }

      if (isNew) {
        await client.createUserProfile({
          display_name: this.displayName,
          avatar_url: imageUrl
        });
      } else if (this.currentEditProfile) {
        await client.updateUserProfile({
          id: this.currentEditProfile.id,
          display_name: this.displayName,
          avatar_url: imageUrl
        });
      }

      this.showCreateModal = false;
      this.showEditModal = false;
      await this.loadProfiles();
    } catch (err) {
      this.error = 'Failed to save profile';
      console.error(err);
    }
  }

  renderModal(isNew: boolean) {
    return html`
    <div class="modal modal-open">
      <div class="modal-box w-full max-w-md">
        <h3 class="font-bold text-xl mb-4">${isNew ? 'Create Profile' : 'Edit Profile'}</h3>

        <label class="block mb-2 text-sm font-medium">Display Name</label>
        <input
          class="input input-bordered w-full mb-4"
          placeholder="Display Name"
          .value=${this.displayName}
          @input=${e => this.displayName = (e.target as HTMLInputElement).value}
        >

        <label class="block mb-2 text-sm font-medium">Avatar Image</label>
        <input
          class="file-input file-input-bordered w-full mb-4"
          type="file"
          @change=${this.handleFileUpload}
        >

        ${this.avatarPreviewUrl ? html`
          <div class="flex justify-center mb-4">
            <img
              src="${this.avatarPreviewUrl}"
              alt="Avatar Preview"
              class="w-24 h-24 rounded-full object-cover border-2 border-primary"
            />
          </div>
        ` : ''}

        ${this.error ? html`<p class="text-error mb-2">${this.error}</p>` : ''}

        <div class="modal-action">
          <button class="btn btn-primary" @click=${() => this.saveProfile(isNew)}>${isNew ? 'Create' : 'Save'}</button>
          <button class="btn" @click=${() => { this.showCreateModal = false; this.showEditModal = false; }}>Cancel</button>
        </div>
      </div>
    </div>
  `;
  }

  render() {
    return html`
    <div class="card bg-base-300 p-6 mb-8">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold">My Profiles</h2>
        <button class="btn btn-primary btn-sm" @click=${this.openCreateModal}>+ New Profile</button>
      </div>

      <ul class="space-y-3">
        ${this.profiles.map(profile => html`
          <li class="flex items-center justify-between p-3 bg-base-100 rounded-lg shadow hover:shadow-md transition">
            <div class="flex items-center gap-4">
              <img
                src="${profile.avatar_url || '/default-avatar.png'}"
                alt="${profile.display_name}"
                class="w-12 h-12 rounded-full object-cover border"
              >
              <div>
                <p class="font-semibold">${profile.display_name}</p>
                ${this.activeProfile?.id === profile.id ? html`
                  <span class="badge badge-primary text-xs mt-1">Active</span>
                ` : ''}
              </div>
            </div>
            <div class="flex gap-2">
              <button class="btn btn-sm btn-outline" @click=${() => this.openEditModal(profile)}>Edit</button>
              <button
                class="btn btn-sm btn-accent ${this.activeProfile?.id === profile.id ? 'btn-disabled' : ''}"
                ?disabled=${this.activeProfile?.id === profile.id}
                @click=${() => this.switchProfile(profile.id)}
              >${this.activeProfile?.id === profile.id ? 'Active' : 'Activate'}</button>
            </div>
          </li>
        `)}
      </ul>
    </div>

    ${this.showCreateModal ? this.renderModal(true) : ''}
    ${this.showEditModal ? this.renderModal(false) : ''}
  `;
  }
}

